'use client';

import React from 'react';
import { motion } from 'framer-motion';
import {
    CheckCircle2,
    ChartSpline,
    Zap,
    TrendingUp,
    Compass,
    ChartNoAxesColumn,
} from 'lucide-react';

// Simplified utility function for combining classes (replacing cn/clsx)
const cn = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

// --- Component Props ---
interface EligibilityCardProps {
    interviewScore?: number;
    assignmentScore?: number;
    taskScore?: number;
    targetScore?: number;
    className?: string;
}

type CardType = 'interview' | 'assignment' | 'task';

/**
 * Small reusable inner card used for Interview / Assignment / Task cards
 */
function InnerCard({
    type,
    label,
    score,
    icon,
    targetScore,
}: {
    type: CardType;
    label: string;
    score: number;
    icon: React.ReactNode;
    targetScore: number;
}) {
    // Calculate percentage, capped at 100% for the progress visualization
    const percentage = Math.max(0, Math.min(Math.round((score / Math.max(1, targetScore)) * 100), 100));
    const met = score >= targetScore;

    // theme per card type (desktop + dark variants)
    const themes: Record<
        CardType,
        {
            bg: string;
            darkBg: string;
            text: string;
            darkText: string;
            ringClass: string;
            darkRingClass: string;
        }
    > = {
        interview: {
            bg: 'bg-green-50',
            darkBg: 'dark:bg-green-600/30',
            text: 'text-slate-900',
            darkText: 'dark:text-gray-100',
            ringClass: met ? 'text-yellow-800' : 'text-green-600',
            darkRingClass: met ? 'dark:text-yellow-300' : 'dark:text-green-300/70',
        },
        assignment: {
            bg: 'bg-purple-50',
            darkBg: 'dark:bg-purple-600/30',
            text: 'text-slate-900',
            darkText: 'dark:text-gray-100',
            ringClass: met ? 'text-green-800' : 'text-purple-600',
            darkRingClass: met ? 'dark:text-green-300' : 'dark:text-purple-300/70',
        },
        task: {
            bg: 'bg-blue-100',
            darkBg: 'dark:bg-sky-600/30',
            text: 'text-slate-900',
            darkText: 'dark:text-gray-100',
            ringClass: met ? 'text-purple-800' : 'text-blue-600/50',
            darkRingClass: met ? 'dark:text-purple-300' : 'dark:text-sky-300/60',
        },
    };

    const theme = themes[type];

    // status badge text and background
    const difference = Math.max(0, targetScore - score);
    const statusText = met ? 'Target Met' : `Need ${difference}%`;
    const statusBg = met ? 'bg-white/15 dark:bg-white/10' : 'bg-white/30 dark:bg-white/5';
    const statusTextColor = met ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-400';

    // circular progress SVG math (viewBox 0 0 100 100)
    const radius = 31;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - percentage / 100);

    return (
        <div
            className={cn(
                'rounded-2xl p-4 shadow-xl transform-gpu hover:-translate-y-1 transition-all duration-300',
                theme.bg,
                theme.darkBg,
                'flex flex-col items-center justify-between min-h-[220px] h-full'
            )}
            role="group"
            aria-label={`${label} card`}
        >
            {/* Top: circular progress */}
            <div className="flex items-center justify-center w-full">
                <div className="relative w-28 h-28">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        {/* Track */}
                        <circle
                            cx="50"
                            cy="50"
                            r={radius}
                            strokeWidth="8"
                            stroke="currentColor"
                            fill="none"
                            className="text-gray-200 dark:text-slate-100/30"
                        />

                        {/* Progress Indicator */}
                        <motion.circle
                            cx="50"
                            cy="50"
                            r={radius}
                            strokeWidth="8"
                            stroke="currentColor"
                            fill="none"
                            strokeDasharray={circumference}
                            strokeDashoffset={dashOffset}
                            initial={{ strokeDashoffset: circumference }}
                            animate={{ strokeDashoffset: dashOffset }}
                            transition={{ duration: 1.2, ease: 'easeOut' }}
                            strokeLinecap="round"
                            className={cn(
                                theme.ringClass,
                                theme.darkRingClass,
                                'drop-shadow-[0_6px_10px_rgba(0,0,0,0.08)]'
                            )} // Apply distinct color based on theme
                        />
                    </svg>

                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <div className={cn('text-2xl font-extrabold', theme.text, theme.darkText)}>{score}%</div>
                    </div>
                </div>
            </div>

            {/* Middle: icon + label */}
            <div className="mt-2 flex flex-col items-center text-center">
                <div
                    className={cn(
                        'p-2 rounded-lg shadow-sm flex items-center justify-center mb-1',
                        met ? 'bg-white/10 dark:bg-white/5' : 'bg-white/20 dark:bg-white/3'
                    )}
                >
                    <div className={cn('w-5 h-5', theme.text, theme.darkText)}>{icon}</div>
                </div>
                <div className={cn('text-sm font-bold tracking-tight', theme.text, theme.darkText)}>{label}</div>
            </div>

            {/* Bottom: status badge */}
            <div className="w-full mt-4 flex justify-center">
                <div
                    className={cn(
                        'px-3 py-1.5 rounded-full text-xs font-semibold uppercase',
                        statusBg,
                        statusTextColor
                    )}
                    aria-hidden
                >
                    {statusText}
                </div>
            </div>
        </div>
    );
}

// --- Main Component ---
export default function EligibilityCard({
    interviewScore = 75, // Default score for visualization
    assignmentScore = 88,
    taskScore = 60,
    targetScore = 80,
    className,
}: EligibilityCardProps) {
    const isEligible =
        interviewScore > 0 &&
        interviewScore >= targetScore &&
        assignmentScore >= targetScore &&
        taskScore >= targetScore;

    const criteria = [
        {
            label: 'Interview Score',
            score: interviewScore,
            icon: <Zap className="h-4 w-4" />,
            type: 'interview' as CardType,
        },
        {
            label: 'Assignments',
            score: assignmentScore,
            icon: <TrendingUp className="h-4 w-4" />,
            type: 'assignment' as CardType,
        },
        {
            label: 'Practical Tasks',
            score: taskScore,
            icon: <Compass className="h-4 w-4" />,
            type: 'task' as CardType,
        },
    ];

    return (
        <div
            className={cn(
                'relative overflow-hidden border-0 mx-auto max-w-7xl',
                // Light bg / Dark bg variants
                'bg-white text-slate-900',
                'dark:bg-black dark:text-gray-100',
                ' md:p-10 rounded-3xl',
                className
            )}
        >
            <div className="p-0">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6 border-b border-slate-200 dark:border-slate-800 pb-4 mb-6">
                    <div className="flex items-center gap-4">
                        <div className="rounded-xl bg-gradient-to-tr from-blue-600 to-cyan-400 p-3 shadow-lg dark:from-sky-400 dark:to-cyan-600">
                            <ChartSpline className="h-7 w-7 text-white" />
                        </div>
                        <div>
                            <h3 className="text-xl md:text-2xl font-extrabold leading-tight text-slate-900 dark:text-gray-100">
                                Final Interview Eligibility Criteria
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-300">
                                Overview of performance across core evaluation areas.
                            </p>
                        </div>
                    </div>

                    <div
                        className={cn(
                            'inline-flex items-center px-4 py-2 rounded-full text-base font-bold shadow-lg uppercase',
                            isEligible ? 'bg-green-600 text-white' : 'bg-rose-600 text-white animate-pulse'
                        )}
                        aria-hidden
                    >
                        {isEligible ? 'Qualified' : 'Requires Focus'}
                    </div>
                </div>

                {/* Body - Enhanced Alignment */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-stretch">
                    {/* Status panel (1/4 width on desktop) */}
                    <div
                        className={cn(
                            'md:col-span-1 flex flex-col gap-4 h-full rounded-xl p-4',
                            'bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-inner'
                        )}
                    >
                        <div className="flex items-center gap-4">
                            <div className="rounded-full bg-slate-50 p-2 shadow-md dark:bg-slate-800">
                                {isEligible ? (
                                    <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-300" />
                                ) : (
                                    <ChartNoAxesColumn className="h-8 w-8 text-rose-600 dark:text-rose-400" />
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="text-sm font-bold text-slate-500 dark:text-slate-400">Overall Status</div>
                                <div
                                    className={cn(
                                        'text-md font-extrabold mt-1 leading-tight',
                                        isEligible ? 'text-green-700 dark:text-green-300' : 'text-rose-700 dark:text-rose-300'
                                    )}
                                >
                                    {isEligible ? 'Target Achieved' : 'Improvement Needed'}
                                </div>
                            </div>
                        </div>

                        <div className="text-sm text-slate-700 dark:text-slate-300 mt-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <strong>Goal:</strong> Reach a minimum of <strong>{targetScore}%</strong> in every category to achieve
                            final interview eligibility.
                        </div>
                    </div>

                    {/* The themed inner cards (3/4 width on desktop, 3 columns inside) */}
                    <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-6 items-stretch">
                        {criteria.map((c) => (
                            <div key={c.label} className="h-full">
                                <InnerCard type={c.type} label={c.label} score={c.score} icon={c.icon} targetScore={targetScore} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
